import { ListResponseModel } from "../../service";
import { Money } from "../common/money";

export const LeadStatus = {
	NotAssigned: "Not Assigned",
	Assigned: "Assigned",
	InProgress: "In Progress",
	Completed: "Completed",
} as const;

export const LevelOfInterest = {
	ReadyToBuy: "Ready to Buy",
	Interested: "Interested",
	NotInterested: "Not Interested",
} as const;

// User model
export interface User {
	id: string;
	firstName: string;
	lastName: string;
	userType: string;
	created: string;
	lastLogin: string;
	phoneNumber: string;
	email: string;
	hasPin: boolean;
	customerDetails?: CustomerDetails;
	employeeDetails?: EmployeeDetails;
}

// CustomerDetails model
export interface CustomerDetails {
	countryCode: string;
}

// EmployeeDetails model
export interface EmployeeDetails {
	companyId: string;
	employeeStatus: string;
	employeeType: string;
}

// Company model
export interface Company {
	id: string;
	name: string;
	businessInfo: string;
	businessType: string;
	rcNumber: string;
	address: string;
	state: string;
	city: string;
	createdAt: string;
	isSuspended: boolean;
	firstLogin: boolean;
	ownerId: string;

	//
	companyStatus: number;
}

// Product category
export interface Category {
	id: string;
	name: string;
	imageUrl: string;
	isCategoryEmpty: boolean;
}

// Product image
export interface ProductImage {
	id: string | number;
	imageUrl: string;
}

// Loan product details
export interface LoanProduct {
	id: string;
	percentageInterestRate: number;
	minimumNumberOfMonths: number;
	maximumNumberOfMonths: number;
	percentageIncreaseMonthStep: number;
	percentageIncrease: number;
	storeCostPrice: number;
}

// Solar product model

// Store product details model
export interface PayAsYouGoDetails {
	costPerKwh: Money;
}

export interface StoreProductDetail {
	id: string;
	productName: string;
	productCapacity: number;
	activeDuration: number;
	packageDescription: string;
	batteryType: string;
	inverterType: string;
	warranty: string;
	batteryVoltage: string;
	installationCostForBungalow: Money;
	installationCostForFloorForStoryBuilding: Money;
	hotDeal: boolean;
	type: string;
	quantityAvailableForSale: number;
	numberOfViews: number;
	createdAt: string;
	images: ProductImage[];
	company: Company;
	categories: Category[];
	priceBreakdown: PriceBreakdown;
	payAsYouGoDetails?: PayAsYouGoDetails;

	//price: number;

	isPaused: boolean;
	isArchived: boolean;
	isDeleted: boolean;
	isDeactivated: boolean;
	productStatus: string;
	companyId: string;

	loanProduct: LoanProduct;
}

export interface DigitalLoadAssessment {
	appliances: AssessmentAppliance[];
	kva: number;
	totalWatts: number;
	kwh: number;
}
export interface StoreProductCategory {
	id: string;
	name: string;
	imageUrl: string;
	isCategoryEmpty: boolean;
}

export interface PriceBreakdown {
	price: Money;
	monthlyPaymentOptions: MonthlyPaymentOption[];
}

export interface MonthlyPaymentOption {
	deposit: Money;
	totalPrice: Money;
	durationInMonths: number;
	monthlyPayment: Money;
}

// Appliance in the assessment
export interface AssessmentAppliance {
	id: string;
	applianceId: number;
	quantity: number;
	isDeleted: boolean;
	usage: number;
	name: string;
}

// Physical load assessment
export interface PhysicalLoadAssessment {
	scheduledDate: string;
	appliances: AssessmentAppliance[];
	kva: number;
	totalWatts: number;
	kwh: number;
}

// Assessment details
export interface AssessmentDetails {
	id: string;
	// recommendationId: string;
	userId: string;
	// appliances: AssessmentAppliance[];
	paymentPlan: PaymentPlan;
	paymentPlanDetails: PaymentPlanDetails;
	installationDetails: InstallationDetails;
	physicalLoadAssessment: PhysicalLoadAssessment;
	digitalLoadAssessment: DigitalLoadAssessment;
	recommendedProducts: StoreProductDetail[];
	selectedProviderId: string;
	selectedProductId: string;
	solarProduct: StoreProductDetail;
	// kva: number;
	// totalWatts: number;
	// kwh: number;

	country: string; // Added missing field
	statusDetails: StatusDetails; // Added missing field
	updatedAt: string; // Added missing field
}

export interface PaymentPlanDetails {
	alternatePowerExpense: Money;
	timelineType: PurchaseTimelineType;
	installmentPlanDetails: InstallmentPlanDetails;
	oneTimePlanDetails: OneTimePlanDetails;
}

export interface InstallmentPlanDetails {
	solarMonthlyRepayment: Money;
	paymentPeriod: number;
}

export interface OneTimePlanDetails {
	oneTimePaymentAmount: Money;
}

export interface InstallationDetails {
	authorizedToInstallOnRoof: boolean;
	numberOfHoursOfElectricityPerDay: number;
	address: string;
}

export interface StatusDetails {
	status: number;
	updatedAt: string;
}

export interface LeadEvent {
	executedBy: string;
	executerName: string;
	timestamp: string;
	type: number;
	typeOfEvent: number;
	workflowId: string;
}

export interface Leads {
	id: string;
	createdAt: string;
	status: number;
	assessmentDetails: AssessmentDetails;
	user: User;
	company: Company;
	events: Array<LeadEvent>;
}

export interface LeadsResponse extends ListResponseModel<Leads> {}

export enum PaymentPlan {
	Undefined = 0,
	Installment = 1,
	OneTime = 2,
	PayAsYouGo = 3,
}

export enum PurchaseTimelineType {
	Undefined = 0,
	ReadyToBuyNow = 1,
	ReadyToBuyInTheNextThreeMonths = 2,
	NeedQuotationForAleterDate = 3,
}

export enum SolarProductType {
	New = 0,
	Existing = 1,
}

export enum AssessmentStatus {
	ChosingProvider = 1, // new Lead
	CheckingDigitalLoadAssessment = 2, // Display all 3 buttons(accept, reject and Physical Load assesment)
	CarringOutPhysicalLoadAssessment = 3,
	SendingProductsThatFit = 4, // Physical load assessment completed.
	Pending = 5, // When its not Accepted by Provider// Edit recommendation is allowed // the only place you can edit a recommendation
	Passive = 6, // When customer has not responded to recommendation by provider.
	NoProvider = 7, // - No Provider
	Rejected = 8, // can not edit recommendation
	Final = 9, // can not edit recommendation
	ScheduledPhysicalLoadAssessment = 10,
	RescheduledPhysicalLoadAssessment = 11,
	OverduePhysicalLoadAssessment = 12,
}

export const STATUS_CONFIG: Partial<Record<AssessmentStatus, any>> = {
	[AssessmentStatus.SendingProductsThatFit]: {
		cssClass: "status_badge_completed",
		text: "Completed",
		canChange: true,
	},
	[AssessmentStatus.CarringOutPhysicalLoadAssessment]: {
		cssClass: "status_badge_progress",
		text: "In Progress",
		canChange: true,
	},
	[AssessmentStatus.ScheduledPhysicalLoadAssessment]: {
		cssClass: "status_badge_scheduled",
		text: "Scheduled",
		canChange: true,
	},
	[AssessmentStatus.RescheduledPhysicalLoadAssessment]: {
		cssClass: "status_badge_rescheduled",
		text: "Rescheduled",
		canChange: true,
	},
	[AssessmentStatus.OverduePhysicalLoadAssessment]: {
		cssClass: "status_badge_overdue",
		text: "Overdue",
		canChange: true,
	},
} as const;

export interface LeadsQueryParams {
	page?: number;
	pageSize?: number;
	statuses?: Array<AssessmentStatus>;
	CompanyIds?: Array<number>;
}

export enum EventType {
	Undefined = 0,
	SolarRecommendation = 1,
	CreditCheck = 2,
	Order = 3,
	OrderInstallment = 4,
}

export enum RecommendationEvent {
	Undefined = 0,
	CreatedDigitalLoadAssessment = 1,
	NoProviderForCustomer = 2,
	SubmitedProviderForCustomer = 3,
	CheckedDigitalLoadAssesment = 4,
	CarriedOutPhysicalLoadAssessment = 5,
	NoProductsForCustomer = 6,
	SendProductsForCustomer = 7,
	SelectedProduct = 8,
	Timeout = 9,
	SatisfiedWithRecommendation = 10,
	RejectedRecommendation = 11,
	SchedulePhysicalLoadAssessment = 12,
	ReschedulePhysicalLoadAssessment = 13,
	OverduePhysicalLoadAssessment = 14,
	StartPhysicalLoadAssessment = 15,
}

export enum OrderInstallationEvent {
	Undefined = 0,
	OrderPlaced = 1,
	DeliveryCompleted = 2,
	InstallationStarted = 3,
	InstallationVerified = 4,
	IssueDetected = 5,
	NoIssues = 6,
}

export enum OrderEvents {
	Undefined = 0,
	OrderCreatedEvent = 1,
	SolarProductReservedEvent = 2,
	SolarProductOutOfStockEvent = 3,
	DatabaseUpdateOnReserveFailedEvent = 4,
	ReserveProductFailedEvent = 5,
	TransactionSuccessfulEvent = 6,
	TransactionFailedEvent = 7,
	SolarProductReleasedEvent = 8,
	DatabaseUpdateOnReleaseFailedEvent = 9,
	ReleaseProductFailedEvent = 10,
	OrderInstallmentCompletedEvent = 11,
}

export enum CreditCheckEvents {
	Undefined = 0,
	StartedCreditCheck = 1,
	CheckedCreditWorthiness = 2,
}

export enum StatusType {
	Pending = 0,
	InProgress = 1,
	Completed = 2,
	Failed = 3,
	Cancelled = 4,
	OnHold = 5,
}

export const getEventTypeString = (type: number, typeOfEvent: number): string => {
	const eventType = type as EventType;

	switch (eventType) {
		case EventType.SolarRecommendation:
			return RecommendationEvent[typeOfEvent] || `Unknown Recommendation Event (${typeOfEvent})`;

		case EventType.CreditCheck:
			return CreditCheckEvents[typeOfEvent] || `Unknown Credit Check Event (${typeOfEvent})`;

		case EventType.Order:
			return OrderEvents[typeOfEvent] || `Unknown Order Event (${typeOfEvent})`;

		case EventType.OrderInstallment:
			return OrderInstallationEvent[typeOfEvent] || `Unknown Installation Event (${typeOfEvent})`;

		default:
			return `Unknown Event Type (${typeOfEvent}:${type})`;
	}
};

export const getTypeString = (type: number): string => {
	const eventType = type as EventType;

	switch (eventType) {
		case EventType.SolarRecommendation:
			return "Solar Recommendation";
		case EventType.CreditCheck:
			return "Credit Check";
		case EventType.Order:
			return "Order";
		case EventType.OrderInstallment:
			return "Order Installation";
		case EventType.Undefined:
			return "Undefined";
		default:
			return `-`;
	}
};

export const formatEventTypeString = (eventTypeString: string): string => {
	if (eventTypeString.includes("Unknown")) {
		return eventTypeString;
	}

	return eventTypeString
		.replace(/([A-Z])/g, " $1") // Insert space before capital letters
		.replace(/^./, str => str.toUpperCase()) // Capitalize first letter
		.trim();
};

export interface StartSolarRecommendationRequest {
	appliances: { id: number; quantity: number; usage: number }[];
	paymentPlan: number;
	paymentPlanDetails: {
		alternatePowerExpense: number | null;
		timelineType: number | null;
		installmentPlanDetails: {
			solarMonthlyRepayment: number | null;
			paymentPeriod: number | null;
		} | null;
		oneTimePlanDetails: {
			oneTimePaymentAmount: number | null;
		} | null;
	} | null;
	installationDetails: {
		authorizedToInstallOnRoof: boolean;
		numberOfHoursOfElectricityPerDay: number;
		address: string;
	};
}
